import { useCallback, useMemo } from 'react';

import { useVendorSession } from '@/features/vendor/useVendorSession';
import { usePlatformSession } from '@/features/platform/usePlatformSession';
import { usePermissions } from '@/shared/permissions/usePermissions';
import {
  useEffectiveAuthority,
  type ReachableSurface,
} from '@/shared/permissions/useEffectiveAuthority';

/**
 * Each portal's OWN permission source, as guidance reads it (CONTRACT §4):
 * the very hooks its shell and pages already use — so a guide's eligibility
 * never disagrees with what the screen shows, and no request is made to decide
 * it (the session each reads is the one the shell has already loaded).
 *
 * None of this is authority. The server decides what anybody may do.
 */
export interface PortalAccess {
  can: (permission: string) => boolean;
  canReach: (surface: ReachableSurface) => boolean;
}

const NEVER = () => false;

/** Tenant: `usePermissions` and the server-decided reach of `useEffectiveAuthority`. */
export function useTenantAccess(): PortalAccess {
  const { permissions } = usePermissions();
  const reach = useEffectiveAuthority();
  const approvals = reach('approvals');

  const can = useMemo(() => {
    const held = new Set(permissions);

    return (permission: string) => held.has(permission);
  }, [permissions]);
  const canReach = useCallback(
    (surface: ReachableSurface) => (surface === 'approvals' ? approvals : false),
    [approvals],
  );

  return useMemo(() => ({ can, canReach }), [can, canReach]);
}

/** Supplier: the portal's own capabilities — deliberately not tenant permissions. */
export function useVendorAccess(): PortalAccess {
  const { user } = useVendorSession();
  const capabilities = user?.capabilities;

  return useMemo(
    () => ({
      can: (capability: string) =>
        capabilities !== undefined &&
        Object.hasOwn(capabilities, capability) &&
        capabilities[capability as keyof typeof capabilities] === true,
      canReach: NEVER,
    }),
    [capabilities],
  );
}

/** Operator: the platform permissions on the operator's own session. */
export function usePlatformAccess(): PortalAccess {
  const { operator } = usePlatformSession();
  const permissions = operator?.permissions;

  return useMemo(() => {
    const held = new Set(permissions ?? []);

    return { can: (permission: string) => held.has(permission), canReach: NEVER };
  }, [permissions]);
}
